import { useCallback, useEffect, useState } from 'react'
import { isRole, roleHasCapability, unmanagedOf } from '@qim/shared'
import { ApiError, fetchQrveyFound, rescanInstances } from '../api.js'
import { pathFor } from '../routing.js'
import { formatAge, formatInstant } from '../format.js'
import { QrveyBadge } from './account-settings.jsx'
import { AccountLink } from './account-link.jsx'
import type { FleetFound, FoundAccount, FoundInstance } from '@qim/shared'
import type { JSX, MouseEvent } from 'react'

/**
 * The Fleet's other half: what each account's last scan found that no run here
 * names (IM-101).
 *
 * ## Why it fills in after the Fleet renders
 *
 * The Fleet is our own records and is on screen in one read. This is a second
 * read — cheap, kept scans only — and it is `diagnostics:read`, which not
 * every role that sees the Fleet holds. So it is asked for separately, only by
 * a role that may see it, and a failure here costs these rows and never the
 * screen.
 *
 * ## Why Scan all is the existing scan, account by account
 *
 * The story's rule: "the per-account scan is reused unchanged". Each account
 * is one `POST …/instances/scan`, run a few at a time, and its rows replace
 * that account's rows as it answers. An account that fails says so on its own
 * line — one expired grant is not a reason to hold back eleven answers.
 */

/** Who may be shown these rows. A convenience, not a control: the route checks. */
export const maySeeFound = (role: string): boolean =>
  isRole(role) && roleHasCapability(role, 'diagnostics:read')

/** How many accounts are scanned at once. Each scan is several AWS calls. */
const SCAN_CONCURRENCY = 3

/** An account whose rescan did not land, and the reason in a reader's words. */
export interface ScanProblem {
  readonly accountKey: string
  readonly label: string
  readonly tenantName: string
  readonly reason: string
}

const reasonFor = (error: unknown): string =>
  error instanceof ApiError && error.status === 403
    ? 'your role does not permit scanning it'
    : error instanceof ApiError && error.status === 404
      ? 'the account is no longer registered'
      : 'the request failed'

export interface FoundState {
  readonly found: FleetFound | undefined
  /** The read itself failed, so these rows are missing — said, not implied. */
  readonly unavailable: boolean
  readonly scanning: boolean
  /** Accounts scanned so far in the current Scan all, out of how many. */
  readonly progress: { readonly done: number; readonly of: number } | undefined
  readonly problems: readonly ScanProblem[]
  readonly scanAll: () => void
}

export const useFound = (baseUrl: string | undefined, role: string): FoundState => {
  const may = baseUrl !== undefined && maySeeFound(role)
  const [found, setFound] = useState<FleetFound | undefined>(undefined)
  const [unavailable, setUnavailable] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [progress, setProgress] = useState<FoundState['progress']>(undefined)
  const [problems, setProblems] = useState<readonly ScanProblem[]>([])

  useEffect(() => {
    if (!may) return
    let live = true
    fetchQrveyFound(baseUrl).then(
      (answer) => {
        if (live) setFound(answer)
      },
      () => {
        if (live) setUnavailable(true)
      },
    )
    return () => {
      live = false
    }
  }, [may, baseUrl])

  const replace = (accountKey: string, next: Partial<FoundAccount>): void => {
    setFound((current) =>
      current === undefined
        ? current
        : {
            ...current,
            accounts: current.accounts.map((a) =>
              a.accountKey === accountKey ? { ...a, ...next } : a,
            ),
          },
    )
  }

  const scanAll = useCallback((): void => {
    if (!may || found === undefined || scanning) return
    const queue = [...found.accounts]
    setScanning(true)
    setProblems([])
    setProgress({ done: 0, of: queue.length })

    const one = async (account: FoundAccount): Promise<void> => {
      try {
        const answer = await rescanInstances(baseUrl, account.tenantId, account.accountKey)
        if (answer.unreachable === true || answer.inventory === undefined) {
          // The kept list was left as it was, so the rows stay; the line says
          // they are as old as they were.
          setProblems((p) => [
            ...p,
            {
              accountKey: account.accountKey,
              label: account.label,
              tenantName: account.tenantName,
              reason: 'could not read the account just now',
            },
          ])
        } else {
          // The scan already left hidden ids out, so nothing to suppress here.
          replace(account.accountKey, {
            scannedAt: answer.inventory.scannedAt,
            instances: unmanagedOf(answer.inventory, new Set()),
          })
        }
      } catch (error: unknown) {
        setProblems((p) => [
          ...p,
          {
            accountKey: account.accountKey,
            label: account.label,
            tenantName: account.tenantName,
            reason: reasonFor(error),
          },
        ])
      } finally {
        setProgress((p) => (p === undefined ? p : { ...p, done: p.done + 1 }))
      }
    }

    const worker = async (): Promise<void> => {
      for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
        await one(next)
      }
    }
    void Promise.all(Array.from({ length: SCAN_CONCURRENCY }, worker)).finally(() => {
      setScanning(false)
    })
  }, [may, baseUrl, found, scanning])

  return { found, unavailable, scanning, progress, problems, scanAll }
}

/** The scan's word for what it saw, as the account screen says it. */
const STATE_WORD: Readonly<Record<string, string>> = {
  running: 'running cluster',
  residue: 'storage only',
  unknown: 'could not tell',
}

/**
 * The controls above the table: how old the answer is, the leftovers filter,
 * and Scan all.
 */
export const FoundControls = ({
  state,
  oldest,
  includeResidue,
  onIncludeResidue,
}: {
  readonly state: FoundState
  readonly oldest: string | undefined
  readonly includeResidue: boolean
  readonly onIncludeResidue: (next: boolean) => void
}): JSX.Element | null => {
  const { found, unavailable, scanning, progress, problems, scanAll } = state
  if (unavailable) {
    return (
      <p className="failure">
        Could not read what the account scans found just now, so instances nobody manages here are
        missing from this list.
      </p>
    )
  }
  if (found === undefined) return null

  const never = found.accounts.filter((a) => a.scannedAt === undefined)
  return (
    <div className="found-controls">
      <p className="hint">
        {found.accounts.length === 0 ? (
          'No accounts are registered, so there is nothing to scan.'
        ) : oldest === undefined ? (
          'No account has been scanned yet, so instances created outside this console are not listed.'
        ) : (
          <>
            Instances nobody manages here come from each account&rsquo;s last scan — the oldest{' '}
            <time dateTime={oldest} title={formatInstant(oldest)}>
              {formatAge(oldest)}
            </time>{' '}
            ago.
          </>
        )}
        {never.length === 0 || oldest === undefined
          ? ''
          : ` ${String(never.length)} account${never.length === 1 ? ' has' : 's have'} never been scanned.`}
      </p>
      <div className="found-actions">
        <label>
          <input
            type="checkbox"
            checked={includeResidue}
            onChange={(event) => onIncludeResidue(event.target.checked)}
          />{' '}
          Include storage-only leftovers
        </label>
        <button
          type="button"
          className="ghost"
          disabled={scanning || found.accounts.length === 0}
          onClick={scanAll}
        >
          {scanning && progress !== undefined
            ? `Scanning ${String(progress.done)} of ${String(progress.of)}…`
            : 'Scan all accounts'}
        </button>
      </div>
      {found.unreadable.length === 0 ? null : (
        <p className="failure">
          Could not read {found.unreadable.map((g) => g.tenantName).join(', ')} just now, so their
          accounts&rsquo; scans are missing.
        </p>
      )}
      {problems.map((p) => (
        <p key={p.accountKey} className="failure">
          Could not scan {p.label} ({p.accountKey}, {p.tenantName}): {p.reason}. Its rows are from
          its previous scan.
        </p>
      ))}
    </div>
  )
}

/**
 * One instance a scan found and no run here names.
 *
 * Same columns as a managed row, so the list reads as one list. What this
 * control plane cannot know — version, search, lifetime — is a dash, and the
 * status says why rather than inventing one: "Not managed here" is a fact about
 * our records, not a verdict about the instance.
 */
export const FoundRow = ({
  instance,
  role,
  onOpen,
}: {
  readonly instance: FoundInstance
  readonly role: string
  readonly onOpen: (path: string) => void
}): JSX.Element => {
  const scanTab = pathFor({
    screen: 'account',
    tenantId: instance.tenantId,
    accountKey: instance.accountKey,
    tab: 'instances',
  })
  const customer = pathFor({ screen: 'customer', tenantId: instance.tenantId })
  const go =
    (to: string) =>
    (event: MouseEvent<HTMLAnchorElement>): void => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      onOpen(to)
    }
  return (
    <tr>
      <td>
        {/* No deployment screen exists for it, so the name is text. A cluster
          that carries no id is named by the cluster, which is what the scan saw. */}
        {instance.deploymentId === ''
          ? (instance.evidence.cluster ?? 'unnamed')
          : instance.deploymentId}
      </td>
      <td>
        <a href={customer} onClick={go(customer)}>
          {instance.tenantName}
        </a>{' '}
        <QrveyBadge kind={instance.tenantKind} />
      </td>
      <td className="mono-cell">
        <AccountLink
          tenantId={instance.tenantId}
          accountKey={instance.accountKey}
          role={role}
          onOpen={onOpen}
        />
      </td>
      <td className="mono-cell">{instance.region === '' ? '—' : instance.region}</td>
      <td>—</td>
      <td>—</td>
      <td>
        <span className="status status-not-managed">
          <span aria-hidden="true">◇</span> Not managed here
        </span>
        <p className="muted">
          {STATE_WORD[instance.state] ?? instance.state} ·{' '}
          {/* Bringing it in needs its config.json, which the account's own
            Instances tab already knows how to read in the browser. */}
          <a href={scanTab} onClick={go(scanTab)}>
            bring in from the account
          </a>
        </p>
      </td>
      {/* The cluster's age from AWS, not ours: we have no runs to date it by.
        A dash where the scan found no cluster to ask. */}
      <td>
        {instance.createdAt === undefined ? (
          '—'
        ) : (
          <time
            dateTime={instance.createdAt}
            title={`Cluster created ${formatInstant(instance.createdAt)}`}
          >
            {formatAge(instance.createdAt)}
          </time>
        )}
      </td>
      <td>—</td>
      <td>
        <span className="muted">No run here</span>
        <p className="muted">
          seen by the scan of{' '}
          <time dateTime={instance.scannedAt}>{formatInstant(instance.scannedAt)}</time>
        </p>
      </td>
    </tr>
  )
}
